import { useMemo, useState } from 'react';
import type { CanonicalRecord, CheckResult } from '@tabservice/tabfile-client';
import { checkResultFromError } from '@tabservice/tabfile-client';
import { Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Field, Textarea } from '@/components/ui/field';
import { Card, SectionHeader } from '@/components/ui/card';
import { ErrorNote, Spinner } from '@/components/ui/feedback';
import { CheckIssues, CheckSummary } from './check-report';
import { DuplicateRecordsNote, duplicateRecordIssues } from './duplicates';
import { tabfile, errorMessage } from '@/lib/api';
import { newIdempotencyKey } from './idempotency';
import { buildNameResolutionPlan, identityColumns } from './name-resolution';
import type { PrecheckResult } from './precheck';
import { ResolveNames, useNameResolution } from './resolve-names';

/** Every field the records use, each reading itself: the records are canonical already. */
function fieldsOf(records: CanonicalRecord[]): Record<string, string> {
  const fields: Record<string, string> = {};
  for (const record of records) for (const key of Object.keys(record)) fields[key] = key;
  return fields;
}

const SAMPLE = `[
  {
    "recordRef": "nec-2026-00001",
    "FILER_FEDERAL_ID_NUMBER": "123456789",
    "RECIPIENT_ID_NUMBER": "987654321",
    "RECIPIENT_FIRST_NAME": "Marisol",
    "RECIPIENT_LAST_NAME": "Okafor",
    "RECIPIENT_ADDRESS1": "22 Fern Street",
    "RECIPIENT_CITY": "Madison",
    "RECIPIENT_STATE": "WI",
    "RECIPIENT_ZIP_CODE": "53703",
    "BOX1_NONEMPLOYEE_COMPENSATION": 4200
  }
]`;

/** For small batches: check the records, then file them without an upload. */
export function PasteJson({
  formType,
  taxYear,
  onFiled,
}: {
  formType: string;
  taxYear: number;
  onFiled: (filingId: string) => void;
}) {
  const [text, setText] = useState(SAMPLE);
  const [parseError, setParseError] = useState<string | null>(null);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [result, setResult] = useState<CheckResult | null>(null);
  const [checking, setChecking] = useState(false);
  const [filing, setFiling] = useState(false);
  /** The records as last validated. Resolve Names reads their names; editing the text clears it. */
  const [checked, setChecked] = useState<CanonicalRecord[] | null>(null);

  // Records with one combined RECIPIENT_NAME get the Resolve Names step, as a file does.
  const review = useMemo<PrecheckResult | null>(
    () =>
      checked
        ? {
            ok: true,
            issues: [],
            depth: 'full',
            detected: { formType, taxYear, recordCount: checked.length },
            mapping: fieldsOf(checked),
            records: checked as Record<string, unknown>[],
          }
        : null,
    [checked, formType, taxYear],
  );
  const names = useNameResolution(review);
  const namesPending = names.plan.required && !names.ready;

  function parseRecords(): CanonicalRecord[] | null {
    try {
      const parsed: unknown = JSON.parse(text);
      if (!Array.isArray(parsed)) {
        setParseError('Send an array of records, even if there is only one.');
        return null;
      }
      if (parsed.length === 0) {
        setParseError('There are no records in that array.');
        return null;
      }
      if (parsed.length > 1000) {
        setParseError('That is more than 1,000 records. Use a file upload instead.');
        return null;
      }
      setParseError(null);
      return parsed as CanonicalRecord[];
    } catch (error) {
      setParseError(error instanceof Error ? `That is not valid JSON: ${error.message}` : 'That is not valid JSON.');
      return null;
    }
  }

  async function runCheck() {
    const records = parseRecords();
    if (!records) return;
    setChecking(true);
    setRequestError(null);
    try {
      const response = await tabfile.POST('/v1/checks', {
        body: { formType, taxYear, records },
      });
      setResult(response);
      setChecked(records);
    } catch (error) {
      setRequestError(errorMessage(error));
    } finally {
      setChecking(false);
    }
  }

  async function fileRecords() {
    const records = parseRecords();
    if (!records) return;
    // Combined names are reviewed before anything is filed, as on a file upload.
    if (!checked && buildNameResolutionPlan(formType, identityColumns(fieldsOf(records)), records).required) {
      setRequestError('These records use RECIPIENT_NAME. Validate them first, then review the names before submitting.');
      return;
    }
    setFiling(true);
    setRequestError(null);
    const nameDecisions = names.forUpload();
    try {
      const response = await tabfile.POST('/v1/e-files', {
        body: { formType, taxYear, records, ...(nameDecisions ? { nameDecisions } : {}) },
        idempotencyKey: newIdempotencyKey(),
      });
      onFiled(response.id);
    } catch (error) {
      const blocked = checkResultFromError(error);
      if (blocked) {
        setResult(blocked);
        setRequestError('Nothing was filed. Fix the blocked records and try again.');
      } else {
        setRequestError(errorMessage(error));
      }
    } finally {
      setFiling(false);
    }
  }

  return (
    <>
      <Card>
        <SectionHeader
          title="Paste records"
          description="For a small batch, paste canonical records instead of uploading a file."
        />
        <Field
          label="Records as JSON"
          hint="An array of canonical records, up to 1,000. Field names match the upload templates."
          error={parseError ?? undefined}
        >
          {(props) => (
            <Textarea
              {...props}
              value={text}
              spellCheck={false}
              onChange={(event) => {
                setText(event.target.value);
                setChecked(null);
              }}
              className="min-h-[280px]"
            />
          )}
        </Field>

        <div className="mt-5 flex flex-wrap items-center gap-3">
          <Button variant="outline" onClick={() => void runCheck()} disabled={checking}>
            {checking ? <Spinner /> : null}
            Validate records
          </Button>
          <Button
            variant="primary"
            onClick={() => void fileRecords()}
            disabled={filing || namesPending || (result ? !result.ok : false)}
          >
            {filing ? <Spinner /> : <Send className="h-4 w-4" aria-hidden="true" />}
            Submit records
          </Button>
          <p className="text-base text-muted-foreground">
            {formType}, tax year {taxYear}
            {namesPending ? ' · Accept the name identifications below to submit.' : null}
          </p>
        </div>

        {requestError ? (
          <div className="mt-4">
            <ErrorNote title="Submission failed" detail={requestError} />
          </div>
        ) : null}
      </Card>

      {result ? (
        <Card>
          <SectionHeader
            title="Validation result"
            description={
              result.ok
                ? 'Nothing is blocked. These records can be submitted.'
                : 'Some records are blocked. Nothing is filed until they are fixed.'
            }
            actions={<CheckSummary result={result} />}
          />
          {duplicateRecordIssues(result.issues).length > 0 ? (
            <div className="pb-5">
              <DuplicateRecordsNote issues={result.issues} />
            </div>
          ) : null}
          <CheckIssues issues={result.issues} />
        </Card>
      ) : null}

      {names.plan.required ? (
        <Card>
          <ResolveNames names={names} />
        </Card>
      ) : null}
    </>
  );
}
